import React, { useEffect } from 'react';
import {
  LayoutDashboard,
  Building2,
  Users,
  FileText,
  FolderOpen,
  Calendar,
  BarChart3,
  Bell,
  Shield,
  Settings,
  ChevronLeft,
  ChevronRight,
  Globe,
  Network,
  ScrollText,
  UserCircle,
  Briefcase,
  Banknote,
  Wrench,
  UserCheck,
  HelpCircle,
  Eye,
  Gift,
  TrendingUp,
  FilePlus,
  FileCode2,
  MessageSquare,
  Sliders,
  Layers,
  Mail,
  ArrowLeftRight,
  Home,
  Crown,
  Inbox,
  Tag,
  BookOpen,
  TrendingDown,
  Receipt,
  Clock,
  Upload,
  MessageCircle,
} from 'lucide-react';
import { NavItem } from './NavItem';
import { NavGroup } from './NavGroup';
import { cn } from '@/utils/cn';
import { useAuthPermissions } from '@/hooks/useAuthPermissions';
import type { UserRole } from '@/types/user';

interface SidebarProps {
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
  isCollapsed?: boolean;
  onCollapseToggle?: () => void;
  onExpand?: () => void;
}

interface NavItemDef {
  icon: React.ElementType;
  label: string;
  to: string;
  roles?: UserRole[];
  hideFromSuperAdmin?: boolean;
}

interface NavGroupDef {
  icon: React.ElementType;
  label: string;
  roles?: UserRole[];
  children: NavItemDef[];
}

// ── Platform section (SUPER_ADMIN only) ──────────────────────────────────────

const superAdminItems: NavItemDef[] = [
  { icon: Globe, label: 'Global Overview', to: '/platform/overview', roles: ['SUPER_ADMIN'] },
  { icon: Network, label: 'Organisations', to: '/platform/organisations', roles: ['SUPER_ADMIN'] },
  {
    icon: BarChart3,
    label: 'Platform Analytics',
    to: '/platform/analytics',
    roles: ['SUPER_ADMIN'],
  },
  { icon: Inbox, label: 'Support Tickets', to: '/platform/support-tickets', roles: ['SUPER_ADMIN'] },
  { icon: Tag, label: 'Pricing Campaigns', to: '/platform/pricing-campaigns', roles: ['SUPER_ADMIN'] },
  { icon: Gift, label: 'Ambassadors', to: '/platform/ambassadors', roles: ['SUPER_ADMIN'] },
];

// ── Standalone items ──────────────────────────────────────────────────────────

const standaloneTop: NavItemDef[] = [
  {
    icon: LayoutDashboard,
    label: 'Dashboard',
    to: '/',
    roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT', 'SUPER_ADMIN'],
  },
  {
    icon: Gift,
    label: 'Referrals',
    to: '/referrals',
    roles: ['ADMIN', 'AGENT', 'PROPERTY_MANAGER', 'SUPER_ADMIN'],
  },
  {
    icon: Network,
    label: 'My Organisation',
    to: '/platform/organisations',
    roles: ['ADMIN'],
    hideFromSuperAdmin: true,
  },
  // Tenant portal — full tenant self-service
  {
    icon: Home,
    label: 'My Portal',
    to: '/tenants/portal',
    roles: ['TENANT'],
  },
  // Owner portal — standalone for OWNER / LANDLORD (not in Finance group)
  {
    icon: UserCircle,
    label: 'Owner Portal',
    to: '/owner-portal',
    roles: ['OWNER', 'LANDLORD'],
  },
  // CLIENT = prospective tenant (pre-lease) — limited view
  {
    icon: UserCheck,
    label: 'My Application',
    to: '/tenants/application',
    roles: ['CLIENT'],
  },
];

const standaloneBottom: NavItemDef[] = [
  { icon: Shield, label: 'Security', to: '/settings/security' },
  { icon: HelpCircle, label: 'Help', to: '/help' },
];

// ── Nav groups ────────────────────────────────────────────────────────────────

const navGroups: NavGroupDef[] = [
  // ── Portfolio ──────────────────────────────────────────────────────────────
  // AGENT can create properties but not manage buildings
  {
    icon: Building2,
    label: 'Portfolio',
    roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
    children: [
      {
        icon: Layers,
        label: 'Buildings',
        to: '/buildings',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
      {
        icon: Building2,
        label: 'Properties',
        to: '/properties',
        roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
      },
    ],
  },

  // ── People ─────────────────────────────────────────────────────────────────
  // Only ADMIN manages users; PM and AGENT handle KYC/onboarding
  {
    icon: Users,
    label: 'People',
    roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
    children: [
      { icon: Users, label: 'Users', to: '/users', roles: ['ADMIN'] },
      {
        icon: UserCheck,
        label: 'KYC & Onboarding',
        to: '/tenants',
        roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
      },
    ],
  },

  // ── Leasing ────────────────────────────────────────────────────────────────
  // AGENT handles leasing and inspections; Move In/Out is PM/ADMIN operational task
  {
    icon: ScrollText,
    label: 'Leasing',
    roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
    children: [
      {
        icon: ScrollText,
        label: 'Leases',
        to: '/leases',
        roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
      },
      {
        icon: ArrowLeftRight,
        label: 'Move In/Out',
        to: '/tenants/move-in-out',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
      {
        icon: Calendar,
        label: 'Inspections',
        to: '/inspections',
        roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
      },
      {
        icon: Wrench,
        label: 'Maintenance',
        to: '/maintenance',
        roles: ['ADMIN', 'PROPERTY_MANAGER', 'VENDOR'],
      },
    ],
  },

  // ── Finance ────────────────────────────────────────────────────────────────
  // ADMIN and PM only; OWNER/LANDLORD use the standalone Owner Portal instead
  {
    icon: Banknote,
    label: 'Finance',
    roles: ['ADMIN', 'PROPERTY_MANAGER'],
    children: [
      {
        icon: FileText,
        label: 'Invoices',
        to: '/invoices',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
      {
        icon: Banknote,
        label: 'Service Charges',
        to: '/service-charges',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
      {
        icon: LayoutDashboard,
        label: 'Dashboard',
        to: '/finance',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
      {
        icon: BookOpen,
        label: 'Accounts',
        to: '/finance/accounts',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
      {
        icon: TrendingDown,
        label: 'Expenses',
        to: '/finance/expenses',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
      {
        icon: Receipt,
        label: 'Receipts',
        to: '/finance/receipts',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
      {
        icon: FileText,
        label: 'Bulk Invoices',
        to: '/finance/bulk-invoices',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
      {
        icon: BarChart3,
        label: 'Reports',
        to: '/finance/reports',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
      {
        icon: Clock,
        label: 'Aging Report',
        to: '/finance/aging',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
      {
        icon: Bell,
        label: 'Reminders',
        to: '/finance/reminders',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
    ],
  },

  // ── CRM & Sales ────────────────────────────────────────────────────────────
  // AGENT is squarely in the agency/sales department
  {
    icon: Briefcase,
    label: 'CRM & Sales',
    roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
    children: [
      { icon: Briefcase, label: 'CRM', to: '/crm', roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'] },
    ],
  },

  // ── Documents ──────────────────────────────────────────────────────────────
  // Templates restricted to ADMIN (policy documents); AGENT can generate & correspond
  {
    icon: FolderOpen,
    label: 'Documents',
    roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
    children: [
      {
        icon: FolderOpen,
        label: 'Document Manager',
        to: '/documents',
        roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
      },
      {
        icon: FilePlus,
        label: 'Generator',
        to: '/documents/generator',
        roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
      },
      {
        icon: FileCode2,
        label: 'Templates',
        to: '/documents/templates',
        roles: ['ADMIN', 'PROPERTY_MANAGER'],
      },
      {
        icon: MessageSquare,
        label: 'Correspondence',
        to: '/documents/correspondence',
        roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
      },
    ],
  },

  // ── Insights ───────────────────────────────────────────────────────────────
  {
    icon: TrendingUp,
    label: 'Insights',
    roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
    children: [
      {
        icon: TrendingUp,
        label: 'Analytics',
        to: '/analytics',
        roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
      },
      {
        icon: BarChart3,
        label: 'Property Reports',
        to: '/reports',
        roles: ['ADMIN', 'PROPERTY_MANAGER', 'AGENT'],
      },
    ],
  },

  // ── Notifications ──────────────────────────────────────────────────────────
  // All roles get Inbox + Preferences; Broadcast is ADMIN-level; Delivery Audit is SA only
  {
    icon: Bell,
    label: 'Notifications',
    children: [
      { icon: Bell, label: 'Inbox', to: '/notifications' },
      { icon: Sliders, label: 'Preferences', to: '/notifications/preferences' },
      {
        icon: Mail,
        label: 'Broadcast',
        to: '/notifications/broadcast',
        roles: ['ADMIN', 'SUPER_ADMIN'],
      },
      {
        icon: Shield,
        label: 'Delivery Audit',
        to: '/notifications/delivery-audit',
        roles: ['SUPER_ADMIN'],
      },
    ],
  },

  // ── Settings ───────────────────────────────────────────────────────────────
  // Group itself now also admits PROPERTY_MANAGER, but every child below still
  // has its own roles: ['ADMIN'] except Import Data — widening the group gate
  // alone doesn't expose any other settings page to PM, only this one item.
  {
    icon: Settings,
    label: 'Settings',
    roles: ['ADMIN', 'PROPERTY_MANAGER'],
    children: [
      { icon: Shield, label: 'Security', to: '/settings/security', roles: ['ADMIN'] },
      { icon: Upload, label: 'Import Data', to: '/import', roles: ['ADMIN', 'PROPERTY_MANAGER'] },
      { icon: Layers, label: 'Assets', to: '/settings/assets', roles: ['ADMIN'] },
      { icon: Globe, label: 'Public API', to: '/settings/public-api', roles: ['ADMIN'] },
      { icon: MessageCircle, label: 'WhatsApp', to: '/settings/whatsapp', roles: ['ADMIN'] },
      { icon: Users, label: 'Assignments', to: '/settings/assignments', roles: ['ADMIN'] },
      { icon: Crown, label: 'My Plan', to: '/settings/plan', roles: ['ADMIN'] },
      { icon: HelpCircle, label: 'FAQs', to: '/settings/faqs', roles: ['ADMIN'] },
      { icon: Eye, label: 'Service Charges', to: '/settings/service-charges', roles: ['ADMIN'] },
      { icon: Banknote, label: 'Rent Disbursement', to: '/settings/rent-disbursement', roles: ['ADMIN'] },
      { icon: Shield, label: 'Audit', to: '/audit', roles: ['ADMIN'] },
    ],
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

const Sidebar: React.FC<SidebarProps> = ({
  isMobileOpen = false,
  onMobileClose,
  isCollapsed = false,
  onCollapseToggle,
  onExpand,
}) => {
  const { hasRole, isSuperAdmin } = useAuthPermissions();

  const isVisible = (item: NavItemDef): boolean => {
    if (item.hideFromSuperAdmin && isSuperAdmin) return false;
    if (item.roles && !isSuperAdmin && !hasRole(item.roles)) return false;
    return true;
  };

  const visibleSuperAdminItems = superAdminItems.filter(isVisible);

  const handleNavItemClick = () => onMobileClose?.();

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileOpen]);

  return (
    <>
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onMobileClose} />
      )}

      <aside
        className={cn(
          'fixed top-16 left-0 z-40 h-[calc(100vh-4rem)] flex flex-col',
          'bg-gradient-to-b from-white to-secondary-50/50 border-r border-secondary-200',
          'transition-all duration-300 ease-in-out',
          'lg:relative lg:top-0 lg:translate-x-0',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full',
          isCollapsed ? 'lg:w-20' : 'lg:w-64',
          'w-64'
        )}
      >
        {/* Collapse toggle */}
        <div
          className={cn(
            'flex items-center h-12 px-4 border-b border-secondary-100 bg-secondary-50/30 flex-shrink-0',
            isCollapsed ? 'justify-center' : 'justify-end'
          )}
        >
          <button
            onClick={onCollapseToggle}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 overflow-x-hidden">
          {/* Platform section — SUPER_ADMIN only */}
          {isSuperAdmin && visibleSuperAdminItems.length > 0 && (
            <div className="px-3 mb-3">
              {!isCollapsed && (
                <p className="px-2 mb-1 text-xs font-semibold uppercase tracking-wider text-secondary-400">
                  Platform
                </p>
              )}
              <div className="space-y-1">
                {visibleSuperAdminItems.map((item) => (
                  <NavItem
                    key={item.to}
                    icon={item.icon}
                    label={item.label}
                    to={item.to}
                    isCollapsed={isCollapsed}
                    onClick={handleNavItemClick}
                  />
                ))}
              </div>
              {!isCollapsed && <div className="mt-3 border-t border-secondary-200" />}
            </div>
          )}

          {/* Standalone top items */}
          <div className={cn('px-3 mb-2 space-y-1', isSuperAdmin && 'mt-2')}>
            {standaloneTop.filter(isVisible).map((item) => (
              <NavItem
                key={item.to}
                icon={item.icon}
                label={item.label}
                to={item.to}
                isCollapsed={isCollapsed}
                onClick={handleNavItemClick}
              />
            ))}
          </div>

          {/* Grouped navigation */}
          <div className="px-3 space-y-1">
            {navGroups.map((group) => {
              // Filter children by role
              const visibleChildren = group.children.filter(isVisible);
              if (visibleChildren.length === 0) return null;

              // Check group-level visibility
              if (group.roles && !isSuperAdmin && !hasRole(group.roles)) return null;

              return (
                <NavGroup
                  key={group.label}
                  icon={group.icon as any}
                  label={group.label}
                  children={visibleChildren as any}
                  isCollapsed={isCollapsed}
                  onExpand={onExpand}
                />
              );
            })}
          </div>

          {/* Standalone bottom items */}
          <div className="px-3 mt-2 space-y-1">
            {standaloneBottom.filter(isVisible).map((item) => (
              <NavItem
                key={item.to}
                icon={item.icon}
                label={item.label}
                to={item.to}
                isCollapsed={isCollapsed}
                onClick={handleNavItemClick}
              />
            ))}
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
